import {
  UseMutateFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  UpdateCsobCompanyDatasetDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_CSOB } from "../domain";
import { ACTION_GET_CSOB_DATASET_COMPARISON } from "../GetCsobDatasetComparison";
import { ACTION_GET_CSOB_FORMS_METADATA } from "../GetCsobFormsMetadata";

import { csobHubApi } from "@api/services";
import {
  ACTION_GET_AVAILIBLE_FORMS,
  ACTION_GET_FORM_DRAFT_DATA,
  ACTION_GET_FORM_SECTIONS,
  DOMAIN_FORM_SUBMISSIONS,
  DOMAIN_FORMS,
  roleGuard,
} from "@api";

export const ACTION_SEND_DATASET_TO_CSOB = "SEND_DATASET_TO_CSOB";

const allowedRoles = [UserRole.Owner, UserRole.Manager, UserRole.Respondent];

const updateCompanyDataset = async (
  props: UpdateCsobCompanyDatasetDto,
): Promise<void> => {
  const response = await csobHubApi.csobHubControllerUpdateCompanyDataset(props);

  return response.data;
};

export const useUpdateCompanyDataset = (
  user: UserProfileDto | null,
): {
  updateCompanyDataset: UseMutateFunction<
    void,
    Error,
    UpdateCsobCompanyDatasetDto,
    unknown
  >;
  updateCompanyDatasetAsync: (props: UpdateCsobCompanyDatasetDto) => Promise<void>;
  updateCompanyDatasetEnabled: boolean;
  updateCompanyDatasetError: Error | null;
  isPendingUpdateCompanyDataset: boolean;
  isSuccessUpdateCompanyDataset: boolean;
} => {
  const queryClient = useQueryClient();
  const enabled = roleGuard(user, allowedRoles);

  const { mutate, mutateAsync, isPending, isSuccess, error } = useMutation({
    mutationKey: [DOMAIN_CSOB, ACTION_SEND_DATASET_TO_CSOB],
    mutationFn: enabled ? updateCompanyDataset : undefined,
    onSuccess: () => {
      // Invalidate dataset comparison
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_CSOB, ACTION_GET_CSOB_DATASET_COMPARISON],
      });

      // Invalidate forms
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_CSOB, ACTION_GET_CSOB_FORMS_METADATA],
      });

      // Invalidate available froms
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_FORMS, ACTION_GET_AVAILIBLE_FORMS],
      });

      // Invalidate current form draft data
      queryClient.invalidateQueries({
        predicate: (query) => {
          return (
            query.queryKey[0] === DOMAIN_FORM_SUBMISSIONS &&
            query.queryKey[1] === ACTION_GET_FORM_DRAFT_DATA
          );
        },
      });

      // Invalidate all form sections
      queryClient.invalidateQueries({
        predicate: (query) => {
          return (
            query.queryKey[0] === DOMAIN_FORMS &&
            query.queryKey[1] === ACTION_GET_FORM_SECTIONS
          );
        },
      });
    },
  });

  return {
    updateCompanyDataset: mutate,
    updateCompanyDatasetAsync: mutateAsync,
    updateCompanyDatasetEnabled: enabled,
    updateCompanyDatasetError: error,
    isPendingUpdateCompanyDataset: isPending,
    isSuccessUpdateCompanyDataset: isSuccess,
  };
};
